import { useEffect, useMemo, useRef, useState } from "react";
import { usePosicionDesplegable } from "../posicionDesplegable";
import Icon from "../Icon";
import * as api from "../../api";
import { hhmm } from "../../utils/hora";

// Enlaza el trabajo con una REUNION del calendario de Outlook.
//
// El cuarto hermano de SelectorPaso, NotionSelector y SelectorIncidencia. Existe
// porque "Reuniones" es el tipo de cuaderno que mas se teclea a mano, y la
// reunion ya esta en el calendario con su titulo y su hora.
//
// Los eventos salen del cache de Postgres (external_calendar_events), no de una
// llamada a Microsoft: pintar una lista no tiene por que depender de que Graph
// conteste. El servidor pone ese cache al dia al leerlo, asi que una reunion
// convocada hace un rato ya esta aqui.
//
// El cache de este modulo es solo para no repetir la llamada al abrir y cerrar
// el desplegable; CADUCA. Antes era `let cache = null` sin mas y una sola
// respuesta vacia —el calendario todavia sin sincronizar, la red caida— se
// quedaba pegada el resto de la sesion: `if (eventos || ...) return` da por
// cargado un array vacio, asi que el buscador ya no volvia a intentarlo y
// recargar la pagina entera era la unica salida.
const CACHE_MS = 60 * 1000;
let cache = null;   // { ts, clave, eventos }

// El cache vale para la ventana que lo lleno y para ninguna otra. Sin la clave,
// abrir una entrada del dia 3 despues de otra del dia 10 reaprovechaba la lista
// del 10 y ofrecia reuniones de otro dia como si fueran del que se esta
// apuntando: peor que no ofrecer nada, porque parece correcto.
const vale = clave => cache && cache.clave === clave && Date.now() - cache.ts < CACHE_MS;

// Una ventana corta a proposito: se cronometra lo de hoy, y como mucho lo que se
// quedo sin apuntar de ayer. Con el calendario entero delante no se encuentra
// nada, y la reunion del mes que viene no se esta trabajando ahora.
const DIAS_ATRAS = 1;
const DIAS_ADELANTE = 1;

// La fecha del reloj de quien mira, componente a componente. Con toISOString()
// la ventana se iba un dia: en Madrid a las 00:30 devuelve todavia el dia
// anterior, y la reunion de esta manana se quedaba fuera del "hasta".
const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
               + `-${String(d.getDate()).padStart(2, "0")}`;
// Un dia del cuaderno, si lo que se esta editando lo tiene: "2026-09-16".
const DIA = /^\d{4}-\d{2}-\d{2}$/;
const esDia = f => DIA.test(String(f || "").slice(0, 10));

// La ventana que se pide al servidor. Pura y exportada porque es justo donde
// estaban los dos fallos —el dia que se iba por UTC y el "hasta" que no llegaba
// a manana— y ninguno de los dos se ve mirando la pantalla: la lista sale, solo
// que le faltan reuniones.
//
// Dos casos, y la diferencia esta en si hay una fecha de la que hablar:
//
//  · SIN fecha es el cronometro, que corre AHORA: ayer, hoy y manana.
//  · CON fecha es una entrada del cuaderno, y entonces manda SU fecha de
//    inicio. Apuntando el trabajo del dia 3 se ofrecian las reuniones de hoy,
//    que no tienen nada que ver: para encontrar la que se busca habia que
//    saberse su titulo de memoria y teclearlo, y aun asi no salia. La lista
//    tiene que ser la del dia que se esta apuntando, sin ayer ni manana
//    alrededor: ahi el "por si acaso" solo mete reuniones de otros dias con
//    nombres parecidos —las semanales se llaman todas igual— y se enlaza la
//    equivocada sin enterarse.
export function ventana(hoy = new Date(), fecha = null) {
  if (esDia(fecha)) {
    const dia = String(fecha).slice(0, 10);
    return { desde: dia, hasta: dia };
  }
  const desde = new Date(hoy); desde.setDate(hoy.getDate() - DIAS_ATRAS);
  const hasta = new Date(hoy); hasta.setDate(hoy.getDate() + DIAS_ADELANTE);
  return { desde: ymd(desde), hasta: ymd(hasta) };
}

// QUE se dice cuando no hay ninguna reunion que ofrecer.
//
// Antes era una sola frase, «No hay reuniones de ayer, hoy ni manana en tu
// calendario», y se decia tambien cuando el calendario no se habia llegado a
// leer. Eso es lo peor que puede hacer un vacio: afirmar algo sobre los datos
// cuando lo que ha pasado es que no hay datos. Manda a buscar el fallo a
// Outlook —donde las reuniones SI estan— en vez de a la conexion, que es donde
// esta roto.
//
// `estado` viene de GET /integrations/microsoft/estado. Mientras no haya
// llegado se dice lo unico que se sabe con certeza: que aqui no hay nada.
export function motivoVacio(estado, hayBusqueda, fecha = null) {
  if (hayBusqueda) return "Sin resultados";
  if (!estado) return "No hay reuniones que enlazar";
  if (!estado.conectado)
    return "No tienes la cuenta de Microsoft conectada. Conéctala en Integraciones.";
  if (estado.error)
    return "No se pudo leer tu calendario de Outlook. Vuelve a conectar la cuenta "
         + "en Integraciones.";
  if (!estado.eventos)
    return "Tu cuenta está conectada, pero el calendario no ha traído ningún evento.";
  // Se nombra EL DIA del que se esta hablando. Decir "de ayer, hoy ni manana"
  // en una entrada del dia 3 es una respuesta a otra pregunta, y deja pensando
  // que el buscador mira donde no debe.
  return esDia(fecha)
    ? `No hay reuniones el ${diaLargo(fecha)} en tu calendario`
    : "No hay reuniones de ayer, hoy ni mañana en tu calendario";
}

// "16 de septiembre" a partir de "2026-09-16", sin pasar por Date: un
// `new Date("2026-09-16")` se lee como UTC y en Madrid puede pintar el dia 15.
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
               "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export function diaLargo(fecha) {
  const partes = String(fecha).slice(0, 10).split("-");
  return `${Number(partes[2])} de ${MESES[Number(partes[1]) - 1]}`;
}

const dia = iso => (iso
  ? new Date(iso).toLocaleDateString("es-ES",
      { weekday: "short", day: "2-digit", month: "short", timeZone: "Europe/Madrid" })
  : "");

export default function SelectorReunion({ eventoId, onChange, fecha = null }) {
  const { desde, hasta } = ventana(new Date(), fecha);
  const clave = `${desde}..${hasta}`;
  const [abierto,  setAbierto]  = useState(false);
  const [q,        setQ]        = useState("");
  const [eventos,  setEventos]  = useState(vale(clave) ? cache.eventos : null);
  const [cargando, setCargando] = useState(false);
  const [fallo,    setFallo]    = useState(false);
  const [estado,   setEstado]   = useState(null);   // por que no hay reuniones
  const ref = useRef(null);
  const panelRef = useRef(null);
  const { estilo: posicion } = usePosicionDesplegable(abierto, ref, panelRef, { altoMax: 280 });

  useEffect(() => {
    if (!abierto) return;
    const fuera = e => {
      if (ref.current && !ref.current.contains(e.target)) { setAbierto(false); setQ(""); }
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  const cargar = async () => {
    if (cargando) return;
    if (eventos && vale(clave)) return;
    setCargando(true);
    try {
      const r = await api.getOutlookEvents(desde, hasta);
      cache = { ts: Date.now(), clave, eventos: Array.isArray(r) ? r : [] };
      setEventos(cache.eventos);
      setFallo(false);
      // Solo cuando no hay nada que ofrecer: es la unica vez que hace falta
      // explicar algo, y asi no se pide en el camino que va bien.
      if (!cache.eventos.length)
        api.getOutlookEstado().then(setEstado).catch(() => {});
    } catch { setFallo(true); }
    finally { setCargando(false); }
  };

  // El evento enlazado puede no estar en la ventana que se pide —una entrada de
  // hace un mes—, asi que se busca en lo cargado y, si no esta, se dice lo que se
  // sabe: que hay una reunion enlazada, aunque no su titulo.
  const enlazado = useMemo(
    () => (eventos || []).find(e => String(e.external_id) === String(eventoId)) || null,
    [eventos, eventoId]);

  // Al abrir con algo ya enlazado se cargan los eventos para poder nombrarlo.
  useEffect(() => { if (eventoId) cargar(); }, [eventoId]);  // eslint-disable-line

  // Cambiar la fecha de la entrada cambia de que dia son las reuniones que se
  // ofrecen, asi que lo que hubiera cargado ya no sirve.
  useEffect(() => { setEventos(vale(clave) ? cache.eventos : null); }, [clave]);  // eslint-disable-line

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    const base = t
      ? (eventos || []).filter(e =>
          String(e.title || "").toLowerCase().includes(t) ||
          String(e.location || "").toLowerCase().includes(t))
      : (eventos || []);
    // De la mas reciente hacia atras: lo que se acaba de terminar es lo que se
    // va a apuntar.
    return [...base]
      .sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime))
      .slice(0, 14);
  }, [q, eventos]);

  const elegir = e => { onChange(e); setAbierto(false); setQ(""); };

  if (eventoId) {
    return (
      <div title={enlazado?.location || ""}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px",
          borderRadius: 8, border: "1px solid #bfdbfe", background: "var(--info-bg)",
          width: "fit-content", maxWidth: "100%" }}>
        <Icon name="calendar-week" size={11} color="#1d4ed8" />
        <span style={{ fontSize: 12, fontWeight: 600, color: "#1d4ed8", flex: 1,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {enlazado ? enlazado.title : "Reunión enlazada"}
        </span>
        {enlazado?.start_datetime && (
          <span style={{ fontSize: 9.5, color: "#3b82f6", flexShrink: 0,
            fontFamily: "var(--mono)" }}>{hhmm(enlazado.start_datetime, "")}</span>
        )}
        <button onMouseDown={e => { e.stopPropagation(); onChange(null); }}
          title="Quitar el enlace"
          style={{ background: "transparent", border: "none", cursor: "pointer",
            color: "#93c5fd", fontSize: 14, lineHeight: 1, padding: "0 0 0 4px" }}>×</button>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div style={{ position: "relative" }}>
        <Icon name="calendar-week" size={11} color="var(--text3)"
          style={{ position: "absolute", left: 9, top: "50%",
            transform: "translateY(-50%)", pointerEvents: "none" }} />
        <input aria-label="Buscar reunión" value={q}
          onChange={e => { setQ(e.target.value); setAbierto(true); }}
          onFocus={() => { setAbierto(true); cargar(); }}
          onKeyDown={e => {
            if (e.key === "Enter" && filtrados.length) { e.preventDefault(); elegir(filtrados[0]); }
            if (e.key === "Escape") { e.stopPropagation(); setAbierto(false); setQ(""); }
          }}
          placeholder={cargando ? "Leyendo el calendario…"
            : esDia(fecha) ? `Buscar la reunión del ${diaLargo(fecha)}…`
            : "Buscar la reunión de tu calendario…"}
          style={{ width: "100%", padding: "5px 8px 5px 26px", borderRadius: 8,
            border: "1px solid var(--border-ctl)", background: "var(--surface)",
            color: "var(--text)", fontSize: 12, fontFamily: "var(--font)" }} />
      </div>

      {abierto && (
        <div ref={panelRef} style={{ zIndex: 200,
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
          boxShadow: "var(--shadow-lg)", width: "min(440px, calc(100vw - 32px))",
          ...posicion }}>
          {cargando && <div style={vacio}>Leyendo el calendario…</div>}
          {!cargando && fallo && (
            <div style={vacio}>No se pudo leer el calendario de Outlook</div>
          )}
          {!cargando && !fallo && !filtrados.length && (
            <div style={vacio}>{motivoVacio(estado, !!q.trim(), fecha)}</div>
          )}
          {filtrados.map(e => (
            <div key={e.external_id} onMouseDown={() => elegir(e)}
              style={{ padding: "7px 12px", cursor: "pointer", display: "flex",
                alignItems: "center", gap: 8, fontSize: 12 }}
              onMouseEnter={ev => { ev.currentTarget.style.background = "var(--bg)"; }}
              onMouseLeave={ev => { ev.currentTarget.style.background = "transparent"; }}>
              <Icon name="calendar-week" size={10} color="#3b82f6" />
              <span style={{ flex: 1, fontWeight: 500, overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title || "(sin título)"}</span>
              <span style={{ fontSize: 9.5, color: "var(--text3)", flexShrink: 0,
                fontFamily: "var(--mono)" }}>
                {dia(e.start_datetime)} {e.is_all_day ? "" : hhmm(e.start_datetime, "")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const vacio = { padding: 12, fontSize: 12, color: "var(--text3)", textAlign: "center" };
